/** Preset swatches tuned for calendar label gradients (light mode). */
export const SERVICE_CALENDAR_COLOR_PRESETS = [
  '#F48FB1',
  '#CE93D8',
  '#90CAF9',
  '#80DEEA',
  '#A5D6A7',
  '#FFE082',
  '#FFAB91',
  '#B0BEC5',
];

/**
 * @param {string | null | undefined} value
 * @returns {string | null} Normalized #RRGGBB or null when unset
 */
export function normalizeCalendarColor(value) {
  const s = String(value || '').trim();
  if (!s) return null;
  const m = /^#?([a-f\d]{6})$/i.exec(s);
  if (!m) return null;
  return `#${m[1].toUpperCase()}`;
}

/**
 * @param {string | null | undefined} hex
 * @returns {number | null} Hue 0–359 for CSS `--label-hue`
 */
export function hexToLabelHue(hex) {
  const normalized = normalizeCalendarColor(hex);
  if (!normalized) return null;

  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === min) return 0;

  const d = max - min;
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return Math.round(h * 360);
}

/**
 * @param {string | null | undefined} hex
 * @returns {{ '--label-hue': number } | {}}
 */
export function labelStyleFromCalendarColor(hex) {
  const hue = hexToLabelHue(hex);
  if (hue == null) return {};
  return { '--label-hue': hue };
}

/**
 * Stable hash palette when no service color is assigned.
 * @param {string} seed
 * @returns {{ '--label-hue': number }}
 */
export function pastelFromSeed(seed) {
  const s = String(seed || 'x');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = s.charCodeAt(i) + ((h << 5) - h);
  }
  return { '--label-hue': Math.abs(h) % 360 };
}

export function labelColorSeed(item) {
  if (item?.type === 'meeting') {
    return `meeting:${item.id}:${item.title || ''}`;
  }
  return item?.serviceId || item?.serviceName || item?.id;
}

/**
 * @param {object} item Appointment or meeting display item
 * @returns {{ '--label-hue': number }}
 */
export function resolveItemLabelStyle(item) {
  if (item?.type !== 'meeting') {
    const hex =
      item?.serviceCalendarColor ??
      item?.service?.calendarColor ??
      null;
    const styled = labelStyleFromCalendarColor(hex);
    if (styled['--label-hue'] != null) return styled;
  }
  return pastelFromSeed(labelColorSeed(item));
}
